import React, { useState } from "react";
import { useInstallPrompt } from "./hooks/useInstallPrompt";
import styled from "styled-components";

const InstallPrompt = () => {
  const { isInstallable, promptInstall } = useInstallPrompt();

  // Use lazy initialization to check localStorage only once on mount
  const [isVisible, setIsVisible] = useState(() => {
    return localStorage.getItem("pwa-install-dismissed") !== "true";
  });

  const handleInstallClick = async () => {
    const accepted = await promptInstall();
    if (accepted) {
      setIsVisible(false);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    // Store dismissal in localStorage to respect user choice
    localStorage.setItem("pwa-install-dismissed", "true");
  };

  if (!isInstallable || !isVisible) {
    return null;
  }

  return (
    <>
      <BannerContainer>
        <BannerWrapper>
          <BannerContent>
            <CloseButton onClick={handleDismiss} aria-label="Dismiss">
              <CloseIcon />
            </CloseButton>

            <ContentWrapper>
              <IconContainer>
                <SmartphoneIcon />
              </IconContainer>

              <TextContent>
                <Title>Install the Paartner app</Title>
                <Description>
                  For quick and easy access to your account
                </Description>

                <InstallButton onClick={handleInstallClick}>
                  <DownloadIcon />
                  INSTALL
                </InstallButton>

                <AdditionalInfo>
                  No sign-in required • Works offline
                </AdditionalInfo>
              </TextContent>
            </ContentWrapper>
          </BannerContent>
        </BannerWrapper>
      </BannerContainer>
    </>
  );
};

export default InstallPrompt;

const BannerContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 1rem;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const BannerWrapper = styled.div`
  max-width: 28rem;
  width: 100%;
  margin: 0 auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  border-radius: var(--Padding-Large, 32px);
  border: 1px solid var(--Color-border-dark, rgba(136, 136, 136, 0.1));
  background: var(--Color-Backgound-Light---2, #e2e8f0);
`;

const BannerContent = styled.div`
  position: relative;
  padding: 1.5rem;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: #212543;
  cursor: pointer;
  transition: color 0.2s;
  padding: 0.25rem;

  &:hover {
    color: #545770ff;
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const IconContainer = styled.div`
  flex-shrink: 0;
  width: 3rem;
  height: 3rem;
  background: #212543;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);

  svg {
    width: 1.5rem;
    height: 1.5rem;
    color: white;
  }
`;

const TextContent = styled.div`
  flex: 1;
`;

const Title = styled.h3`
  color: #212543;
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  font-size: 1.125rem;
  margin: 0 0 0.25rem 0;
`;

const Description = styled.p`
  color: #212543;
  font-family: Montserrat, sans-serif;
  font-size: 0.875rem;
  margin: 0 0 1rem 0;
`;

const InstallButton = styled.button`
  width: 100%;
  background: white;
  color: #212543;
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;

  &:hover {
    background: #d8dae7ff;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const AdditionalInfo = styled.p`
  color: #212543;
  font-size: 0.75rem;
  margin: 0.75rem 0 0 0;
  text-align: center;
  font-family: Montserrat, sans-serif;
`;

const InstalledContainer = styled.div`
  padding: 1rem;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 0.5rem;
  text-align: center;

  svg {
    width: 2rem;
    height: 2rem;
    color: #16a34a;
    margin: 0 auto 0.5rem;
    display: block;
  }
`;

const InstalledTitle = styled.p`
  color: #166534;
  font-weight: 500;
  margin: 0 0 0.25rem 0;
`;

const InstalledSubtext = styled.p`
  color: #16a34a;
  font-size: 0.875rem;
  margin: 0;
`;

// SVG Icons as components
const DownloadIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const SmartphoneIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
    <line x1="12" y1="18" x2="12.01" y2="18" />
  </svg>
);
